<div class="space-y-4">
  @if (!hideTitle) {
  <div class="flex items-center justify-between">
    <h2 class="text-lg font-semibold text-gray-900 dark:text-white">{{ catalog.title }}</h2>
  </div>
  }

  @if (error) {
  <div
    class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
    role="alert"
  >
    {{ error }}
  </div>
  }

  @if (savedNotice && !error) {
  <output
    class="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-300"
  >
    {{ savedNotice }}
  </output>
  }

  @if (draft) {
  <form
    class="rounded-3xl border border-gray-200/80 bg-white/40 p-4 shadow-sm dark:border-[#191e3a] dark:bg-dark/10 md:p-6"
    (ngSubmit)="submit()"
  >
    <h3 class="mb-4 text-base font-semibold text-gray-900 dark:text-white">
      {{ isNew ? 'Add ' + catalog.singular : 'Edit ' + catalog.singular }}
    </h3>

    <div class="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
      @for (f of catalog.fields; track trackByField($index, f)) {
      <label class="flex flex-col gap-1.5 text-sm">
        <span class="flex items-center gap-2 font-medium text-gray-900 dark:text-white">
          {{ f.label }}
          @if (isLocked(f)) {
          <span
            class="rounded bg-gray-100 px-1.5 text-[11px] font-medium text-gray-600 dark:bg-white/10 dark:text-gray-300"
            >read only</span
          >
          }
        </span>

        @switch (f.kind) {
          @case ('number') {
          <input
            type="number"
            class="form-input w-full rounded-xl border-gray-200 bg-white dark:border-[#191e3a] dark:bg-[#0e1726]"
            [(ngModel)]="draft[f.key]"
            [name]="f.key"
            [disabled]="isLocked(f)"
            [required]="!!f.required"
          />
          }
          @case ('boolean') {
          <label class="relative inline-flex h-6 w-11 cursor-pointer items-center">
            <input
              type="checkbox"
              class="peer sr-only"
              [(ngModel)]="draft[f.key]"
              [name]="f.key"
              [disabled]="isLocked(f)"
            />
            <span
              class="h-6 w-11 rounded-full bg-gray-200 transition peer-checked:bg-[#303030] dark:bg-white/10"
            ></span>
            <span
              class="absolute left-0.5 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5"
            ></span>
          </label>
          }
          @case ('enum') {
          @if (f.searchable) {
          <app-searchable-select
            [options]="fieldOptions(f)"
            [(ngModel)]="draft[f.key]"
            [name]="f.key"
            [disabled]="isLocked(f)"
            [clearable]="!f.required"
            placeholder="— Select —"
          />
          } @else {
          <select
            class="form-select w-full rounded-xl border-gray-200 bg-white dark:border-[#191e3a] dark:bg-[#0e1726]"
            [(ngModel)]="draft[f.key]"
            [name]="f.key"
            [disabled]="isLocked(f)"
            [required]="!!f.required"
          >
            <option value="">— Select —</option>
            @for (o of fieldOptions(f); track o.value) {
            <option [value]="o.value">{{ o.label }}</option>
            }
          </select>
          }
          }
          @case ('tags') {
          <input
            type="text"
            class="form-input w-full rounded-xl border-gray-200 bg-white dark:border-[#191e3a] dark:bg-[#0e1726]"
            [ngModel]="tagsToText(draft[f.key])"
            (ngModelChange)="onTagsInput(f, $event)"
            [name]="f.key"
            [disabled]="isLocked(f)"
            [placeholder]="f.placeholder || 'Comma separated'"
          />
          }
          @default {
          <input
            type="text"
            class="form-input w-full rounded-xl border-gray-200 bg-white dark:border-[#191e3a] dark:bg-[#0e1726]"
            [(ngModel)]="draft[f.key]"
            [name]="f.key"
            [disabled]="isLocked(f)"
            [required]="!!f.required"
            [placeholder]="f.placeholder || ''"
          />
          }
        }

        @if (f.hint) {
        <span class="text-[11px] leading-tight text-gray-500 dark:text-gray-400">{{ f.hint }}</span>
        }
      </label>
      }
    </div>

    <div class="mt-6 flex items-center justify-end gap-3">
      <button
        type="button"
        class="btn inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-black shadow-none hover:bg-gray-50 disabled:opacity-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5"
        (click)="cancel()"
        [disabled]="saving"
      >
        Cancel
      </button>
      <button
        type="submit"
        class="btn inline-flex items-center gap-2 rounded-full bg-[#303030] px-5 py-2.5 text-sm font-medium text-white hover:bg-black disabled:opacity-50 dark:bg-gray-900 dark:hover:bg-black"
        [disabled]="saving"
      >
        {{ saving ? 'Saving…' : 'Save' }}
      </button>
    </div>
  </form>
  } @else {
  <app-list-module-surface
    [config]="surfaceConfig"
    [filters]="listFilters"
    [errorMessage]="''"
    [loading]="loading"
    [meta]="paginationMeta"
    [rowCount]="pagedRecords.length"
    (filtersChange)="onListFiltersChange($event)"
    (pageChange)="onListPageChange($event)"
  >
    @if (facetField) {
    <ng-container toolbarFiltersExtra>
      <div class="relative">
        <label class="sr-only" [attr.for]="'entityCatalogFacet'">{{ facetField.label }}</label>
        <select
          id="entityCatalogFacet"
          name="entityCatalogFacet"
          class="form-select rounded-full border-white-light bg-white py-3 px-4 text-white-dark dark:border-[#191e3a] dark:bg-dark"
          [ngModel]="facetValue"
          (ngModelChange)="onFacetChange($event)"
        >
          @for (opt of facetOptions; track opt.value) {
          <option [value]="opt.value">{{ opt.label }}</option>
          }
        </select>
      </div>
    </ng-container>
    }

    <ng-container toolbarActions>
      @if (canCreate) {
      <button
        type="button"
        class="btn inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-black shadow-none hover:bg-gray-50 dark:border-[#191e3a] dark:bg-[#0e1726] dark:text-white dark:hover:bg-white/5"
        (click)="startCreate()"
      >
        <icon-plus-circle class="size-5" />
        <span>Add {{ catalog.singular }}</span>
      </button>
      }
      @for (tab of toolbarTabs; track tab.key) {
      <button
        type="button"
        class="btn inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium shadow-none"
        [class.border-gray-300]="activeToolbarTab !== tab.key"
        [class.bg-white]="activeToolbarTab !== tab.key"
        [class.text-black]="activeToolbarTab !== tab.key"
        [class.hover:bg-gray-50]="activeToolbarTab !== tab.key"
        [class.dark:border-[#191e3a]]="activeToolbarTab !== tab.key"
        [class.dark:bg-[#0e1726]]="activeToolbarTab !== tab.key"
        [class.dark:text-white]="activeToolbarTab !== tab.key"
        [class.dark:hover:bg-white/5]="activeToolbarTab !== tab.key"
        [class.border-[#303030]]="activeToolbarTab === tab.key"
        [class.bg-[#303030]]="activeToolbarTab === tab.key"
        [class.text-white]="activeToolbarTab === tab.key"
        [class.dark:bg-gray-900]="activeToolbarTab === tab.key"
        (click)="toolbarTabChange.emit(tab.key)"
      >
        {{ tab.label }}
      </button>
      }
    </ng-container>

    <app-list-module-table
      listTable
      variant="surface"
      [columns]="columns"
      [rows]="pagedRecords"
      [loading]="loading"
      [emptyMessage]="emptyMessage"
      [rowTrackKey]="catalog.idKey"
    >
      @for (f of listFields; track f.key) {
      <ng-template [listCellDef]="f.key" let-row="row">
        @if (isStatusColumn(f)) {
        <div
          class="inline-flex items-center rounded-full py-1 px-3"
          [ngClass]="statusBadgeClass(row[f.key])"
        >
          {{ statusLabel(row[f.key], f) }}
        </div>
        } @else {
        <span class="text-gray-900 dark:text-white">{{ displayValue(row, f) }}</span>
        }
      </ng-template>
      }

      <ng-template listCellDef="actions" let-row="row">
        @if (canEdit || canDelete || canRestore) {
        <div class="inline-flex items-center justify-end gap-1">
          @if (canEdit) {
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-primary hover:bg-primary/10"
            title="Edit"
            aria-label="Edit"
            (click)="startEdit(row)"
          >
            <icon-edit class="h-4 w-4" />
          </button>
          }
          @if (canDelete) {
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-danger hover:bg-danger/10"
            title="Delete"
            aria-label="Delete"
            (click)="delete.emit(row)"
          >
            <icon-trash-lines class="h-4 w-4" />
          </button>
          }
          @if (canRestore) {
          <button
            type="button"
            class="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#b8860b] hover:bg-[#FFD85F]/20"
            title="Restore"
            aria-label="Restore"
            (click)="restore.emit(row)"
          >
            <icon-restore class="h-4 w-4" />
          </button>
          }
        </div>
        }
      </ng-template>
    </app-list-module-table>
  </app-list-module-surface>
  }
</div>
